// REQ-P1-06b: イベントジャンル一覧の契約型付きAPI。

import { api, type paths } from './client';
import { ApiStatusError } from './templates';

type GenreListResponse = NonNullable<
  paths['/genres']['get']['responses'][200]['content']
>['application/json'];
type GenreMutationRequest = NonNullable<
  paths['/genres']['post']['requestBody']
>['content']['application/json'];
type GenreReorderRequest = NonNullable<
  paths['/genres/reorder']['put']['requestBody']
>['content']['application/json'];

export type GenreRow = GenreListResponse[number];

type ApiTextResult = {
  data?: string | unknown;
  error?: string | unknown;
  response: Response;
};

function getApiErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (typeof error === 'string') {
    try {
      const parsed = JSON.parse(error);
      if (parsed && typeof parsed === 'object') {
        const record = parsed as Record<string, unknown>;
        const message = record.message || record.error;
        return typeof message === 'string' ? message : error;
      }
    } catch {
      return error;
    }
  }
  if (typeof error !== 'object') return undefined;
  const record = error as Record<string, unknown>;
  const message = record.message || record.error;
  return typeof message === 'string' ? message : undefined;
}

function throwApiError(status: number, error: unknown, fallbackMessage: string): never {
  const statusError = new ApiStatusError(status);
  statusError.message = getApiErrorMessage(error) || fallbackMessage;
  throw statusError;
}

function parseJsonText(raw: unknown): Record<string, unknown> {
  if (raw && typeof raw === 'object') return raw as Record<string, unknown>;
  if (typeof raw !== 'string' || !raw.trim()) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

function parseGenreMutationResult(
  result: ApiTextResult,
  fallbackMessage: string
): Record<string, unknown> {
  const payload = parseJsonText(result.data);
  if (!result.response.ok) {
    throwApiError(result.response.status, result.error || payload, fallbackMessage);
  }
  return payload;
}

export async function listGenres(): Promise<GenreRow[]> {
  const { data, response } = await api.GET('/genres');
  if (!response.ok || !data) throw new ApiStatusError(response.status);
  return Array.isArray(data) ? data : [];
}

export async function createGenre(body: GenreMutationRequest): Promise<Record<string, unknown>> {
  const result = await api.POST('/genres', {
    body,
    parseAs: 'text',
  });
  return parseGenreMutationResult(result, 'ジャンルの追加に失敗しました');
}

export async function updateGenre(
  id: number | string,
  body: GenreMutationRequest
): Promise<Record<string, unknown>> {
  const result = await api.PUT('/genres/{id}', {
    params: { path: { id: Number(id) } },
    body,
    parseAs: 'text',
  });
  return parseGenreMutationResult(result, 'ジャンルの更新に失敗しました');
}

export async function deleteGenre(id: number | string): Promise<Record<string, unknown>> {
  const result = await api.DELETE('/genres/{id}', {
    params: { path: { id: Number(id) } },
    parseAs: 'text',
  });
  return parseGenreMutationResult(result, 'ジャンルの削除に失敗しました');
}

export async function reorderGenres(
  body: GenreReorderRequest
): Promise<Record<string, unknown>> {
  const result = await api.PUT('/genres/reorder', {
    body,
    parseAs: 'text',
  });
  return parseGenreMutationResult(result, '並び順の更新に失敗しました');
}
